import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { HelpRequestStatus, HelpRequestListItem } from '@tenfour/contracts'
import { accountHelpApi, adminHelpApi } from './api'

// Both clients hit the same endpoints; the only difference is which JWT we
// send. We key queries by audience so account and admin caches don't
// stomp each other (they really do show different data — accounts see
// only their own requests, admins see all).
type Audience = 'account' | 'admin'

function clientFor(audience: Audience) {
  return audience === 'admin' ? adminHelpApi : accountHelpApi
}

const REFETCH_MS = 15_000

// ── Queries ──────────────────────────────────────────────────────────────────

export function useHelpRequests(audience: Audience, status?: HelpRequestStatus) {
  return useQuery({
    queryKey: ['help', audience, 'list', status ?? null],
    queryFn: () => clientFor(audience).list(status),
    refetchInterval: REFETCH_MS,
  })
}

export function useUnreadHelpCount(audience: Audience) {
  const q = useHelpRequests(audience)
  const items: HelpRequestListItem[] = q.data ?? []
  const unread = items.reduce((sum, r) => sum + (r.unreadCount ?? 0), 0)
  return { unread, isLoading: q.isLoading }
}

export function useHelpRequest(audience: Audience, requestId: string | null) {
  // GET detail flips isRead server-side, so the list's unreadCount needs to
  // re-fetch. We invalidate from mutations (replies, status changes) and rely
  // on the list's 15s poll to catch up in the no-mutation case — that keeps
  // this hook compatible with TanStack Query v5 which removed onSuccess.
  return useQuery({
    queryKey: ['help', audience, 'detail', requestId],
    queryFn: () => clientFor(audience).get(requestId as string),
    enabled: !!requestId,
    refetchInterval: REFETCH_MS,
  })
}

// ── Mutations ────────────────────────────────────────────────────────────────

export function useCreateHelpRequest(audience: Audience = 'account') {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { subject: string; body: string; topic?: string }) =>
      clientFor(audience).create(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['help', audience] })
    },
  })
}

export function useAddHelpMessage(audience: Audience, requestId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: string) => clientFor(audience).addMessage(requestId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['help', audience, 'detail', requestId] })
      queryClient.invalidateQueries({ queryKey: ['help', audience, 'list'] })
    },
  })
}

export function useUpdateHelpRequestStatus(audience: Audience, requestId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (status: HelpRequestStatus) =>
      clientFor(audience).updateStatus(requestId, status),
    // Optimistic flip so the dropdown reflects the new value instantly.
    onMutate: async (status) => {
      const detailKey = ['help', audience, 'detail', requestId]
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous = queryClient.getQueryData(detailKey) as unknown
      queryClient.setQueryData(detailKey, (old: any) =>
        old ? { ...old, status } : old,
      )
      return { previous }
    },
    onError: (_err, _status, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(['help', audience, 'detail', requestId], context.previous)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['help', audience, 'detail', requestId] })
      queryClient.invalidateQueries({ queryKey: ['help', audience, 'list'] })
    },
  })
}
